
import CircularProgress from '@mui/material/CircularProgress'
import TextField from '@mui/material/TextField'
import CopyText from 'components/Composer/Credits/CopyText'
import { useEffectUpdateOnly } from 'hooks/useEffectUpdateOnly'
import { Article } from 'interfaces'
import * as React from 'react'
import { useEffect } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { of as observableOf, Subject } from 'rxjs'
import { catchError, filter, map, mergeMap } from 'rxjs/operators'
import {
  composerContentItemSelector,
  composerLinkSelector,
  composerResetKeySelector,
  genericPostSelector
} from 'services/compose/selectors'
import { fetchHashtagsSuggestions } from 'services/compose/suggest/actions'
import { message } from 'services/snackbar'
import { ARTICLE_TYPE, BRAND_LINKEDIN } from 'shared'
import { StoreThunkDispatch } from 'store/state'
import styles from './HashtagsGenerator.pcss'

const MIN_PROMPT_LENGTH = 10

export function HashtagsGenerator() {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const intl = useIntl()
  const initialLoadComplete$ = React.useRef(false)
  const content = useSelector(composerContentItemSelector)
  const originalCaption = React.useMemo(() => {
    const useContentCaption = content?.feed.type !== BRAND_LINKEDIN || content?.status?.indexOf('@') === -1
    return useContentCaption ? content?.status : undefined
  }, [content?.feed.type, content?.status])
  const link = useSelector(composerLinkSelector)
  const statusText = useSelector(genericPostSelector).status.text
  const composerResetKey = useSelector(composerResetKeySelector)
  const get$ = React.useRef<Subject<{ prompt: string, linkUrl?: string }>>(undefined)
  const [loading, setLoading] = React.useState(false)
  const [hashtags, setHashtags] = React.useState<string[]>([])
  const [prompt, setPrompt] = React.useState<string>('')
  const [withRefresh, setWithRefresh] = React.useState(false)
  const [refreshDisabled, setRefreshDisabled] = React.useState(false)

  React.useEffect(() => {
    setHashtags([])
    setPrompt('')
  }, [composerResetKey])

  const url = React.useMemo(() => {
    if (link?.type === 'link') {
      return link.url
    }
    return content?.type === ARTICLE_TYPE ? (content as Article).sourceLink : undefined
  }, [link, content])

  const text = React.useMemo(() => {
    const value = prompt.trim()
    const composerStatus = statusText.trim()

    return value || composerStatus || originalCaption || ''
  }, [originalCaption, prompt, statusText])

  const isEnabled = Boolean(url || text)

  useEffectUpdateOnly(() => {
    setWithRefresh(true)
    setRefreshDisabled(false)
  }, [statusText, url, prompt])

  useEffect(() => {
    get$.current = new Subject()
    get$.current
      .pipe(
        map(({ prompt, linkUrl }) => {
          if (!linkUrl && prompt.length < MIN_PROMPT_LENGTH) {
            const msg = `Please enter URL in the text box on the left or a prompt of at least ${MIN_PROMPT_LENGTH} characters.`
            dispatch(message(msg, 'info'))
            return false
          }

          return { prompt, linkUrl }
        }),
        filter(Boolean),
        mergeMap(({ prompt, linkUrl }: { prompt: string, linkUrl: string }) => {
          setLoading(true)
          setRefreshDisabled(true)
          setWithRefresh(true)
          return dispatch(fetchHashtagsSuggestions(prompt, linkUrl)).pipe(
            catchError((res) => {
              return observableOf({ error: res.response?.error || {} })
            })
          )
        })
      ).subscribe(response => {
        setLoading(false)
        if (response.error) {
          const msg = response.error.message || intl.formatMessage({ id: 'errors.generic' })
          dispatch(message(msg, 'error'))
          return
        }
        setHashtags(response.items.filter((item: any) => Boolean(item)))
      })

    return () => {
      get$.current?.unsubscribe()
    }
  }, [dispatch, intl])

  React.useEffect(() => {
    if (!initialLoadComplete$.current) {
      const value = statusText.trim() || originalCaption || ''
      if (value.length >= MIN_PROMPT_LENGTH || url) {
        get$.current?.next({ linkUrl: url, prompt: value })
      }
      initialLoadComplete$.current = true
    }
  }, [originalCaption, prompt, statusText, url])

  const submit = () => {
    if (isEnabled) {
      get$.current?.next({ linkUrl: url, prompt: text })
    }
  }

  const onCopy = () => {
    dispatch(message(intl.formatMessage({ id: 'notifications.status-copied' }), 'info'))
  }

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPrompt(e.target.value)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const value = (e.target as HTMLInputElement).value.trim()
    if (e.key === 'Enter') {
      submit()
    }
  }

  return (
    <div data-testid="composer-hashtags">
      <div className={styles.top}>
        <TextField
          value={prompt}
          classes={{ root: styles['prompt-box'] }}
          placeholder="Add keywords and press Enter"
          onChange={onChange}
          onKeyDown={onKeyDown}
        />
        {/* TEMP: hide regenerate btn {withRefresh && (
          <IconButton
            className={styles['btn-refresh']}
            disabled={refreshDisabled || loading || !isEnabled}
            onClick={submit}
          >
            <IconRefresh fontSize="small" />
          </IconButton>
        )} */}
      </div>
      {!isEnabled && (
        <p className={styles.hint}>Add keywords above to get hashtag suggestions.</p>
      )}
      <div className={styles.list}>
        {loading && (
          <div className={styles.loader}><CircularProgress size="20px" /></div>
        )}
        {!loading && hashtags.map((text, index) => (
          <HashtagListItem
            key={index}
            text={text}
            onCopy={onCopy}
          />
        ))}
      </div>
    </div>
  )
}

const HashtagListItem = ({ text, onCopy }: { text: string, onCopy: () => void }) => {
  const handleClick = () => {
    navigator.clipboard.writeText(text).then(onCopy)
  }
  return (
    <CopyText
      messageText={text}
      title={`RECOMMENDED HASHTAGS`}
      showFullText
      className={styles.hashtagListItem}
      onTextCopied={onCopy}
      onClick={handleClick}
    />
  )
}
